<template>
  <div>
    <v-row>
      <h1 class="ml-4">Teams</h1>
      <v-spacer></v-spacer>
      <v-btn color="primary" @click="openDialog">Create Team</v-btn>
    </v-row>

    <v-spacer></v-spacer>
    <v-card class="mt-3">
      <v-card-title>
        <v-text-field
          v-model="search"
          placeholder="Type a team's name to search..."
          outlined
          clearable
          counter
        ></v-text-field>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="teams.records"
          :loading="isLoading"
          :search="search"
          transition="scale-transition"
          class="new-detault-table teams-view"
        >
          <template v-slot:item.createdAt="{ item }">
            <h3 color="primary">{{ item.createdAt | getFormattedDate }}</h3>
          </template>
          <template v-slot:item.updatedAt="{ item }">
            <h3 color="primary">{{ item.updatedAt | getFormattedDate }}</h3>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn
              fab
              x-small
              dark
              class="mr-2"
              @click="openUpdateDialog(item)"
              color="primary"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              fab
              x-small
              dark
              class="ml-2"
              @click="deleteItem(item)"
              color="blue darken-2"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="showDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ dialogTitle }}</span>
        </v-card-title>
        <v-card-text>
          <v-text-field v-model="teamModel.name" label="Name"></v-text-field>
          <v-progress-linear
            v-if="isCreating || isUpdating"
            indeterminate
            color="blue"
          ></v-progress-linear>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="blue darken-1"
            text
            @click="closeDialog"
            :disabled="isCreating || isUpdating"
            >Cancel</v-btn
          >
          <v-btn
            color="blue darken-1"
            text
            @click="saveTeam"
            :disabled="isCreating || isUpdating"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { gql } from "apollo-boost";

export default {
  name: "ViewTeam",
  data() {
    return {
      headers: [
        {
          text: "Name",
          align: "start",
          sortable: false,
          value: "name",
        },
        { text: "Created Date", value: "createdAt" },
        { text: "Updated Date", value: "updatedAt" },
        { text: "Actions", align: "right", sortable: false, value: "actions" },
      ],
      search: "",
      teams: [],
      totalRecords: 0,
      isLoading: false,
      showDialog: false,
      isCreating: false,
      isUpdating: false,
      updateObject: null,
      id: "",
      teamModel: {
        name: "",
      },
    };
  },

  computed: {
    dialogTitle() {
      return this.updateObject ? "Update Team" : "Create Team";
    },
  },

  apollo: {
    teams: {
      query: gql`
        query Teams($limit: Int!, $skip: Int!, $query: JSON!) {
          teams(limit: $limit, skip: $skip, query: $query) {
            records {
              id
              name
              createdAt
              updatedAt
            }
            count
          }
        }
      `,
      fetchPolicy: "network-only",
      variables() {
        const limit = 300;
        const skip = 0;
        return {
          limit: limit,
          skip: skip,
          query: {},
        };
      },
      watchLoading(isLoading) {
        this.isLoading = isLoading;
      },
    },
    me: {
      query: gql`
        query {
          me {
            id
          }
        }
      `,
      update(data) {
        this.id = data.me.id;
      },
    },
  },

  methods: {
    openDialog() {
      this.updateObject = null;
      this.teamModel.name = "";
      this.showDialog = true;
    },

    openUpdateDialog(item) {
      this.updateObject = item;
      this.teamModel.name = item.name;
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
    },

    async saveTeam() {
      if (this.updateObject) {
        this.updateTeam();
      } else {
        this.createTeam();
      }
    },

    async createTeam() {
      this.isCreating = true;

      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation createTeam($team: TeamCreateInput!) {
              createTeam(team: $team) {
                id
              }
            }
          `,
          variables: {
            team: {
              name: this.teamModel.name,
              createdBy: this.id,
            },
          },
        });

        if (response && response.data.createTeam) {
          this.$swal("Success!", "Team was created", "success").then(() => {
            this.showDialog = false;
            this.teamModel.name = "";
            this.$apollo.queries.teams.refetch();
          });
        }
      } catch (e) {
        this.$swal("Error", e.message || "Something went wrong", "error");
      } finally {
        this.isCreating = false;
      }
    },

    async updateTeam() {
      this.isUpdating = true;

      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation updateTeam($team: TeamUpdateInput!) {
              updateTeam(team: $team) {
                id
              }
            }
          `,
          variables: {
            team: {
              id: this.updateObject.id,
              name: this.teamModel.name,
              updatedBy: this.id,
            },
          },
        });

        if (response && response.data.updateTeam) {
          this.$swal("Success!", "Team was updated", "success").then(() => {
            this.showDialog = false;
            this.updateObject = null;
            this.teamModel.name = "";
            this.$apollo.queries.teams.refetch();
          });
        }
      } catch (e) {
        this.$swal("Error", e.message || "Something went wrong", "error");
      } finally {
        this.isUpdating = false;
      }
    },

    deleteItem(item) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085D6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then(async (result) => {
        if (result.value) {
          try {
            const response = await this.$apollo.mutate({
              mutation: gql`
                mutation ($id: ID!) {
                  deleteTeam(id: $id) {
                    id
                    deleted
                  }
                }
              `,
              variables: {
                id: item.id,
              },
            });

            if (response) {
              this.$swal(
                "Success!",
                "Team was successfully deleted",
                "success"
              );
              this.$apollo.queries.teams.refetch();
            }
          } catch (e) {
            this.$swal("Error", e.message || "Something went wrong", "error");
          }
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped></style>
